'use client';
import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import { Dumbbell, Zap, Shield, Clock } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


type AuthTab = 'login' | 'register' | 'forgot-password';

export default function AuthPageClient() {
  const [activeTab, setActiveTab] = useState<AuthTab>('login');

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel — Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden flex-col justify-between p-12">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/20 to-background" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-12 h-12 rounded-2xl gradient-primary flex items-center justify-center">
              <Dumbbell size={24} className="text-primary-foreground" />
            </div>
            <div>
              <span className="text-xl font-bold text-foreground">MuscleStation</span>
              <p className="text-xs text-muted-foreground">Smart Gym Check-In System</p>
            </div>
          </div>

          <div className="mb-12">
            <h1 className="text-hero text-foreground mb-4 leading-tight">
              Your gym,<br />
              <span className="text-primary">smarter.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-md">
              Scan the QR code at the entrance, check in instantly, and track every workout session.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 max-w-md">
            {[
              { icon: Zap, title: 'Instant Check-In', desc: 'One tap to log your arrival and departure' },
              { icon: Clock, title: 'Session Tracking', desc: 'Automatic duration calculation for every visit' },
              { icon: Shield, title: 'Admin Approved', desc: 'Secure access controlled by your gym manager' },
            ].map((feat) => {
              const Icon = feat.icon;
              return (
                <div key={`feat-${feat.title}`} className="flex items-start gap-4 p-4 rounded-2xl bg-card/50 border border-border/50">
                  <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{feat.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{feat.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-xs text-muted-foreground">
            © 2026 MuscleStation
          </p>
        </div>
      </div>

      {/* Right Panel — Auth Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col justify-center px-6 py-12 lg:px-12 xl:px-16">
        {/* Mobile logo */}
        <div className="flex items-center gap-3 mb-8 lg:hidden">
          <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
            <Dumbbell size={20} className="text-primary-foreground" />
          </div>
          <span className="text-lg font-bold text-foreground">MuscleStation</span>
        </div>

        {/* Tab switcher — hidden on forgot-password view */}
        {activeTab !== 'forgot-password' && (
          <div className="flex p-1 bg-muted rounded-2xl mb-8">
            {(['login', 'register'] as AuthTab[]).map((tab) => (
              <button
                key={`tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === tab
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'login' && (
          <LoginForm
            onSwitchToRegister={() => setActiveTab('register')}
            onForgotPassword={() => setActiveTab('forgot-password')}
          />
        )}
        {activeTab === 'register' && (
          <RegisterForm onSwitchToLogin={() => setActiveTab('login')} />
        )}
        {activeTab === 'forgot-password' && (
          <ForgotPasswordForm onBack={() => setActiveTab('login')} />
        )}
      </div>
    </div>
  );
}